import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import type { Booking } from '@/types';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { PRICING_TYPE_LABELS } from '@/features/booking/utils/bookingPricing';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { bookingStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import { formatBookingWhen } from '@/shared/utils/bookingWhen';

interface BookingCardProps {
  booking: Booking;
  onClick?: () => void;
}

export function BookingCard({ booking, onClick }: BookingCardProps) {
  const travelDate = formatBookingWhen(booking);
  const travelerName = booking.travelerContact?.name ?? booking.travelerName;
  const destination = booking.destinationLabel ?? booking.destinationName ?? 'Trip';

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...dashboardPanelSx,
        p: 0,
        mb: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        '&:hover': onClick
          ? { borderColor: 'primary.100', boxShadow: 'var(--shadow-md)', transform: 'translateY(-1px)' }
          : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box
          sx={{
            width: 4,
            flexShrink: 0,
            bgcolor: 'secondary.main',
          }}
        />
        <Box sx={{ flex: 1, p: 2.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'primary.main',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                }}
              >
                {(travelerName ?? 'T')[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {travelerName ?? 'Traveler'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.35, color: 'text.secondary' }}>
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
                  <Typography variant="body2" color="inherit">
                    {travelDate}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary' }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
                  <Typography variant="body2" color="inherit" noWrap>
                    {destination}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <StatusBadge
                status={bookingStatusToBadge(booking.status)}
                label={formatStatusLabel(booking.status)}
                sx={{ mb: 1 }}
              />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ letterSpacing: '-0.02em', color: 'primary.main' }}
              >
                {formatGuidePrice(booking.grossAmount, booking.currency)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="body2" color="text.secondary">
            {PRICING_TYPE_LABELS[booking.pricingType]} · {booking.travelerCount} traveler
            {booking.travelerCount > 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
